import React from 'react';
import { Plus, Sparkles, FileText, SlidersHorizontal, Image as ImageIcon, Type, Square } from 'lucide-react';
import { genId } from './genId';

/**
 * Builds a freshly-stamped visual record for the given object kind.
 * Object kinds (image, rich_text, shape) are draggable to header/footer zones.
 */
function makeImageViz(id, z) {
  return {
    id, vizType: 'image', title: 'Image',
    rowDims: [], colDim: '', valueMeasures: [], filterDims: [],
    imageUrl: '', imageFit: 'contain', imageBorder: false,
    freePosition: { x: 50, y: 50, w: 300, h: 200 }, zIndex: z,
  };
}

function makeRichTextViz(id, z) {
  return {
    id, vizType: 'rich_text', title: 'Text',
    rowDims: [], colDim: '', valueMeasures: [], filterDims: [],
    textContent: 'Enter text here...', textSize: 16, textColor: '',
    textAlign: 'left', textBold: false, textItalic: false,
    freePosition: { x: 80, y: 80, w: 250, h: 150 }, zIndex: z,
  };
}

function makeShapeViz(id, z) {
  return {
    id, vizType: 'shape', title: 'Shape',
    rowDims: [], colDim: '', valueMeasures: [], filterDims: [],
    shapeType: 'rectangle', shapeFill: '#3b82f6', shapeBorder: '#1e40af',
    shapeOpacity: 0.3, shapeBorderRadius: 12,
    freePosition: { x: 60, y: 60, w: 200, h: 150 }, zIndex: z,
  };
}

function makeAiTextViz(id) {
  return {
    id, vizType: 'ai_text', title: 'AI Insight',
    rowDims: [], colDim: '', valueMeasures: [], filterDims: [],
    aiPrompt: '', aiRerunOnFilter: true, aiRerunOnClick: false, aiContent: '',
  };
}

/**
 * Visual-add toolbar — a row of small icon buttons that appears at the top of the Visuals tab.
 * Each button stamps a new visual into state (and optionally registers a drag image for header/footer drops).
 */
export function VisualToolbar({
  addVisual,
  setVisuals, setActiveVisual,
  setTextBlocks, setAnnotations,
  getMaxZIndex,
}) {
  const stampObjectViz = (kind) => {
    const id = genId(kind === 'image' ? 'img_' : kind === 'rich_text' ? 'txt_' : 'shp_');
    const z = getMaxZIndex() + 1;
    const builder = kind === 'image' ? makeImageViz : kind === 'rich_text' ? makeRichTextViz : makeShapeViz;
    setVisuals(prev => [...prev, builder(id, z)]);
    setActiveVisual(id);
  };

  const stampAiText = () => {
    const id = genId('ai_');
    setVisuals(prev => [...prev, makeAiTextViz(id)]);
    setActiveVisual(id);
  };

  const stampTextBlock = () => {
    setTextBlocks(prev => [...prev, { id: genId('txt_'), text: 'Enter text here...', fontSize: 14 }]);
  };

  const stampAnnotation = () => {
    setAnnotations(prev => [...prev, { id: genId('ann_'), text: '', dataPoint: null }]);
  };

  return (
    <div className="flex gap-1">
      <button type="button" onClick={() => addVisual()} className="text-primary" data-testid="add-visual-btn" title="Add chart"><Plus className="w-3 h-3" /></button>
      <button type="button" onClick={stampAiText} className="text-indigo-400 hover:text-indigo-300" title="Add AI Text Block" data-testid="add-ai-text-btn"><Sparkles className="w-3 h-3" /></button>
      <button type="button" onClick={stampTextBlock} className="text-muted-foreground hover:text-foreground" title="Add text block" data-testid="add-textblock-btn"><FileText className="w-3 h-3" /></button>
      <button type="button" onClick={stampAnnotation} className="text-amber-400 hover:text-amber-300" title="Add annotation" data-testid="add-annotation-btn"><SlidersHorizontal className="w-3 h-3" /></button>
      <button type="button" draggable onDragStart={e => e.dataTransfer.setData('text/plain', 'image')} onClick={() => stampObjectViz('image')} className="text-teal-400 hover:text-teal-300" title="Add Image (or drag to header/footer)" data-testid="add-image-btn"><ImageIcon className="w-3 h-3" /></button>
      <button type="button" draggable onDragStart={e => e.dataTransfer.setData('text/plain', 'rich_text')} onClick={() => stampObjectViz('rich_text')} className="text-sky-400 hover:text-sky-300" title="Add Text (or drag to header/footer)" data-testid="add-text-btn"><Type className="w-3 h-3" /></button>
      <button type="button" draggable onDragStart={e => e.dataTransfer.setData('text/plain', 'shape')} onClick={() => stampObjectViz('shape')} className="text-rose-400 hover:text-rose-300" title="Add Shape (or drag to header/footer)" data-testid="add-shape-btn"><Square className="w-3 h-3" /></button>
    </div>
  );
}
